/* import __COLOCATED_TEMPLATE__ from './card-customization.hbs'; */
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { SIGNATURE_FONT_MEMBERS } from '@banking/ui/models/common/card-customization';
import TextRenderer, { BUSINESS_BANKING_CARD_SIGNATURE_RECT, BUSINESS_BANKING_LOWER_RIGHT_RENDER_STRATEGY } from '@banking/ui/utils/text-renderer';
import assert, { assertInstanceOf } from '@banking/ui/utils/assert';
import { nonNullable } from '@banking/ui/utils/predicates';
import { containsEmoji, stripEmoji } from '@banking/ui/utils/string';
const MAXIMUM_CUSTOMIZATION_LENGTH = 21;
function loadSignatureFont(load) {
  return async function (font) {
    try {
      await load(font);
      return font;
    } catch {
      return null;
    }
  };
}
const loadSignatureFonts = async load => {
  const all = await Promise.all(SIGNATURE_FONT_MEMBERS.map(loadSignatureFont(load)));
  return all.filter(nonNullable);
};

// error is only populated if **non** of the fonts load.
// signatureFonts will be populated with all successfully loaded fonts.

const DEFAULT_INITIAL_NEW_CUSTOMIZATION_ACTIVE = true;
let CardCustomizationComponent = (_class = class CardCustomizationComponent extends Component {
  constructor(...args) {
    super(...args);
    // internal state
    _initializerDefineProperty(this, "fontLoading", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "fontsLoaded", _descriptor3, this);
    _initializerDefineProperty(this, "customizationActive", _descriptor4, this);
    _initializerDefineProperty(this, "selectedFont", _descriptor5, this);
    _initializerDefineProperty(this, "signatureFonts", _descriptor6, this);
    _initializerDefineProperty(this, "signatureTextInput", _descriptor7, this);
    _initializerDefineProperty(this, "signature", _descriptor8, this);
  }
  get customizationImageDataURL() {
    if (this.signature == null) {
      return '';
    }
    if (this.fontsLoaded) {
      this.signature.text = this.customizationActive ? this.signatureText : ''; // eslint-disable-line ember/no-side-effects
      this.signature.fontFamily = this.selectedFont || ''; // eslint-disable-line ember/no-side-effects
    }
    return this.signature.toDataURL();
  }
  get signatureText() {
    return stripEmoji(this.signatureTextInput).slice(0, MAXIMUM_CUSTOMIZATION_LENGTH);
  }
  get signatureFieldInvalid() {
    return !/\S/.test(this.signatureTextInput) || containsEmoji(this.signatureTextInput) || this.signatureTextInput.length > MAXIMUM_CUSTOMIZATION_LENGTH;
  }
  get nextButtonDisabled() {
    return this.customizationActive ? this.signatureFieldInvalid : false;
  }
  get signatureFieldHelperMessage() {
    const value = this.signatureTextInput;
    if (!/\S/.test(value)) {
      return this.intl.t('banking.checking.onboarding.cardCustomization.validation.empty');
    }
    if (containsEmoji(value)) {
      return this.intl.t('banking.checking.onboarding.cardCustomization.validation.emoji');
    }
    if (value.length > MAXIMUM_CUSTOMIZATION_LENGTH) {
      return this.intl.t('banking.checking.onboarding.cardCustomization.validation.charOver', {
        number: value.length - MAXIMUM_CUSTOMIZATION_LENGTH
      });
    }
    return this.intl.t('banking.checking.onboarding.cardCustomization.validation.charLessThanOrEqual', {
      number: MAXIMUM_CUSTOMIZATION_LENGTH - value.length
    });
  }
  async loadFonts() {
    const signatureFontsLoaded = await loadSignatureFonts(this.fontLoading.load);
    assert(signatureFontsLoaded.length > 0);
    this.signatureFonts = signatureFontsLoaded;
    const selectedFont = signatureFontsLoaded.includes(this.args.initialSignatureFont) ? this.args.initialSignatureFont : signatureFontsLoaded[0];
    this.selectedFont = selectedFont;
    this.signature = new TextRenderer(100, this.selectedFont, '', BUSINESS_BANKING_CARD_SIGNATURE_RECT, BUSINESS_BANKING_LOWER_RIGHT_RENDER_STRATEGY);
    this.fontsLoaded = true;
    return signatureFontsLoaded;
  }
  initialize() {
    // NOTE(steckel): We have to explicitly load our fonts to guarantee the
    // fonts we intend to render via the canvas-based TextRenderer are
    // available at runtime. If we don't, the default browser font could be
    // used.
    this.loadFonts().then(val => this.args.onSignatureFontsLoad?.(null, val)).catch(err => this.args.onSignatureFontsLoad?.(err, null));
  }
  onSignAsChange(event) {
    // NOTE (steckel): Not sure why I need the specific generic...
    const val = assertInstanceOf(event, CustomEvent).detail.value;
    this.signatureTextInput = val;
    this.args.onSignatureTextChange?.(val);
  }
  onCustomizationActiveCheckboxChange(event) {
    const val = assertInstanceOf(event, CustomEvent).detail.current === true;
    this.customizationActive = val;
    this.args.onNewCustomizationActive?.(val);
  }
  onFontRowSelected(val) {
    assert(typeof val === 'string');
    assert(SIGNATURE_FONT_MEMBERS.includes(val));
    // TODO (steckel): Find out how to narrow with type casting.
    const signatureFont = val;
    this.selectedFont = signatureFont;
    this.args.onSignatureFontChange?.(val);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "fontLoading", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "fontsLoaded", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "customizationActive", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.initialNewCustomizationActive ?? DEFAULT_INITIAL_NEW_CUSTOMIZATION_ACTIVE;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "selectedFont", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "signatureFonts", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "signatureTextInput", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.initialSignatureText;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "signature", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "initialize", [action], Object.getOwnPropertyDescriptor(_class.prototype, "initialize"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onSignAsChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onSignAsChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onCustomizationActiveCheckboxChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onCustomizationActiveCheckboxChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onFontRowSelected", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onFontRowSelected"), _class.prototype), _class);
export { CardCustomizationComponent as default };